import { useState } from "react";
import { Crown, Search, Shield, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { tierDisplayName, type Tier } from "@/lib/tier";
import type { AuditAction, LookupUser } from "./types";

type Props = {
  reloadRoster: () => Promise<void>;
  writeAudit: (action: AuditAction, targetUserId: string, label: string, email: string | null) => Promise<void>;
};

/** "Grant admin role" card: email lookup + grant admin/super admin actions. */
export const GrantAdminCard = ({ reloadRoster, writeAudit }: Props) => {
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupResult, setLookupResult] = useState<LookupUser | null | "not-found">(null);
  const [grantBusy, setGrantBusy] = useState(false);

  const lookup = async () => {
    const email = lookupEmail.trim().toLowerCase();
    if (!email) return;
    setLookupBusy(true);
    setLookupResult(null);
    const { data, error } = await supabase.functions.invoke("admin-lookup-user", { body: { email } });
    setLookupBusy(false);
    if (error) {
      toast.error(`Lookup failed: ${error.message}`);
      return;
    }
    const u = (data as { user: LookupUser | null } | null)?.user ?? null;
    setLookupResult(u ?? "not-found");
  };

  const grant = async (target: LookupUser, role: "admin" | "super_admin") => {
    setGrantBusy(true);
    const { error } = await supabase.from("user_roles").insert({ user_id: target.id, role });
    if (error) {
      toast.error(`Could not grant: ${error.message}`);
    } else {
      const action: AuditAction = role === "super_admin" ? "grant_super_admin_role" : "grant_admin_role";
      const label = target.workspace_label || target.display_name || target.email || "user";
      await writeAudit(action, target.id, label, target.email);
      toast.success(`${label} granted ${role === "super_admin" ? "super admin" : "admin"}`);
      await reloadRoster();
      setLookupResult({
        ...target,
        roles: Array.from(new Set([...target.roles, role, ...(role === "super_admin" ? ["admin"] : [])])),
      });
    }
    setGrantBusy(false);
  };

  const lookupHasAdmin = lookupResult && lookupResult !== "not-found" && lookupResult.roles.includes("admin");
  const lookupHasSuper = lookupResult && lookupResult !== "not-found" && lookupResult.roles.includes("super_admin");

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-primary" /> Grant admin role
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Look up a user by their account email. They must have already signed up.
        </p>
        <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
          <Input
            type="email"
            placeholder="email@example.com"
            value={lookupEmail}
            onChange={(e) => setLookupEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !lookupBusy) void lookup(); }}
            disabled={lookupBusy}
          />
          <Button onClick={lookup} disabled={lookupBusy || !lookupEmail.trim()}>
            <Search className="h-4 w-4" />
            {lookupBusy ? "Looking up…" : "Look up"}
          </Button>
        </div>

        {lookupResult === "not-found" && (
          <p className="text-sm text-muted-foreground">No account found for that email.</p>
        )}

        {lookupResult && lookupResult !== "not-found" && (
          <div className="rounded-md border border-border p-3 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">
                {lookupResult.workspace_label || lookupResult.display_name || lookupResult.email || "Unknown"}
              </span>
              {lookupResult.account_tier && (
                <Badge variant="secondary">{tierDisplayName(lookupResult.account_tier as Tier)}</Badge>
              )}
              {lookupHasSuper && <Badge className="gap-1"><Crown className="h-3 w-3" /> Super Admin</Badge>}
              {lookupHasAdmin && !lookupHasSuper && <Badge variant="secondary" className="gap-1"><Shield className="h-3 w-3" /> Admin</Badge>}
            </div>
            <p className="text-xs text-muted-foreground">{lookupResult.email}</p>
            <div className="flex flex-wrap gap-2 pt-1">
              {!lookupHasAdmin && (
                <Button size="sm" disabled={grantBusy} onClick={() => grant(lookupResult, "admin")}>
                  <Shield className="h-4 w-4" /> Grant admin
                </Button>
              )}
              {!lookupHasSuper && (
                <Button size="sm" variant="outline" disabled={grantBusy} onClick={() => grant(lookupResult, "super_admin")}>
                  <Crown className="h-4 w-4" /> Grant super admin
                </Button>
              )}
              {lookupHasSuper && (
                <p className="text-sm text-muted-foreground">Already a super admin.</p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
